'use client'

import { Filter, Search, Building2, Bookmark } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type ShareRoomCommunityStepAudienceProps = {
  audienceMethod: 'filter' | 'quick'
  onAudienceMethodChange: (method: 'filter' | 'quick') => void
  onContinue: () => void
}

export function ShareRoomCommunityStepAudience({
  audienceMethod,
  onAudienceMethodChange,
  onContinue,
}: ShareRoomCommunityStepAudienceProps) {
  return (
    <div className="space-y-4 py-1">
      <p className="text-sm text-slate-600">
        Start with attribute filters, or switch to quick search by name. Account bulk-add and saved segments are coming
        next.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onAudienceMethodChange('filter')}
          className={cn(
            'rounded-xl border-2 p-4 text-left transition-colors',
            audienceMethod === 'filter'
              ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/15'
              : 'border-slate-200 bg-white hover:border-slate-300'
          )}
        >
          <Filter className="mb-2 h-5 w-5 text-blue-600" aria-hidden />
          <div className="font-semibold text-slate-900">Filter by attributes</div>
          <p className="mt-1 text-xs text-slate-500">
            Account type, job title, contacts without an account, and teammates
          </p>
        </button>
        <button
          type="button"
          onClick={() => onAudienceMethodChange('quick')}
          className={cn(
            'rounded-xl border-2 p-4 text-left transition-colors',
            audienceMethod === 'quick'
              ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/15'
              : 'border-slate-200 bg-white hover:border-slate-300'
          )}
        >
          <Search className="mb-2 h-5 w-5 text-blue-600" aria-hidden />
          <div className="font-semibold text-slate-900">Search by name or email</div>
          <p className="mt-1 text-xs text-slate-500">Find people quickly when you already know who to add</p>
        </button>
        <div className="rounded-xl border border-dashed border-slate-200 p-4 opacity-70">
          <Building2 className="mb-2 h-5 w-5 text-slate-400" aria-hidden />
          <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-700">
            By account
            <Badge variant="secondary" className="text-[10px] font-semibold">
              Soon
            </Badge>
          </div>
          <p className="mt-1 text-xs text-slate-500">Invite everyone at selected accounts</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-200 p-4 opacity-70">
          <Bookmark className="mb-2 h-5 w-5 text-slate-400" aria-hidden />
          <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-700">
            Saved segment
            <Badge variant="secondary" className="text-[10px] font-semibold">
              Soon
            </Badge>
          </div>
          <p className="mt-1 text-xs text-slate-500">Reuse a defined audience list</p>
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="button" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </div>
  )
}
